import { useState, useMemo, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

import {
  useGetNewCounsellingQuery,
  useGetLiveCounsellingStatusQuery,
  useTranferTeacherPostMutation,
} from './counselling.services';
import { alertService } from '../../shared/services/alertService';

/**
 * Migrated from Angular `TransferFilterComponent`
 * (src/app/views/pages/counselling/transfer-filter).
 *
 * Route: /counselling/transfer-filter
 */

const TransferFilter = () => {
  // Selected counselling from dropdown
  const [counsellingId, setCounsellingId] = useState<string | null>(null);
  // Confirmation dialog visibility
  const [viewDetails, setViewDetails] = useState(false);
  // Global filter
  const [globalFilter, setGlobalFilter] = useState('');

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: counsellingRaw } = useGetNewCounsellingQuery();

  const { data: liveRaw, isFetching: loading } =
    useGetLiveCounsellingStatusQuery(
      { counsellingId },
      { skip: !counsellingId }
    );

  const [tranferTeacher] = useTranferTeacherPostMutation();

  // ── Derived data ─────────────────────────────────────────────────────────

  const counsellingList = useMemo(() => {
    if (!counsellingRaw?.dataStatus) return [];
    return counsellingRaw.result.map((item: any) => ({
      label: item.Title,
      value: item.IndexId,
    }));
  }, [counsellingRaw]);

  const tableData = useMemo(() => {
    if (!counsellingId || !liveRaw?.dataStatus) return [];
    return liveRaw.result;
  }, [counsellingId, liveRaw]);

  const pendingCount = useMemo(() => {
    return tableData.filter(
      (x: any) => x.afterCnsnUpd === 0 || x.afterCnsnUpd === '0'
    ).length;
  }, [tableData]);

  const isSubmitVisible = useMemo(() => {
    return tableData.some(
      (item: any) => item.afterCnsnUpd !== 1 && item.afterCnsnUpd !== '1'
    );
  }, [tableData]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCounsellingChange = useCallback((value: string | null) => {
    setCounsellingId(value);
    setGlobalFilter('');
  }, []);

  const handleSave = useCallback(async () => {
    if (!counsellingId) return;

    try {
      const res = await tranferTeacher({ CnsId: counsellingId }).unwrap();
      if (res.dataStatus) {
        alertService.success('Updated Successfully');
        setViewDetails(false);
      } else {
        alertService.error('Update Failed');
      }
    } catch {
      alertService.error('Update Failed');
    }
  }, [counsellingId, tranferTeacher]);

  // ── Status column template ───────────────────────────────────────────────

  const statusBodyTemplate = (rowData: any) => {
    const isTransferred =
      rowData.afterCnsnUpd === 1 || rowData.afterCnsnUpd === '1';
    return (
      <span style={{ color: isTransferred ? 'green' : 'orange' }}>
        {isTransferred ? 'Transferred' : 'Pending'}
      </span>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="card mt-3 p-4">
        <div className="col-md-3 col-12 mb-2">
          <label className="fw-bold d-block mb-1">Counselling List</label>
          <Dropdown
            value={counsellingId}
            options={counsellingList}
            optionLabel="label"
            optionValue="value"
            placeholder="Please select Counselling"
            filter
            showClear
            className="w-100"
            onChange={(e) => handleCounsellingChange(e.value)}
          />
        </div>

        <div className="mt-3">
          <DataTable
            value={tableData}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            showGridlines
            stripedRows
            resizableColumns
            rowHover
            globalFilter={globalFilter}
            globalFilterFields={[
              'teacher_id',
              'name',
              'NewDesign',
              'school_name',
              'newSchlNme',
            ]}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            emptyMessage={<div className="text-center">No Records Found</div>}
            responsiveLayout="scroll"
            header={
              <div className="row">
                <div className="col-md-9 col-12"></div>
                <div className="col-md-3 col-12 text-end">
                  <span className="p-input-icon-left">
                    <InputText
                      type="text"
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      placeholder="Search keyword"
                    />
                  </span>
                </div>
              </div>
            }
          >
            <Column
              header="S.No"
              body={(_data, options) => options.rowIndex + 1}
              style={{ width: '60px' }}
            />
            <Column
              field="teacher_id"
              header="Teacher ID"
              sortable
              style={{ width: '120px' }}
            />
            <Column
              field="name"
              header="Teacher Name"
              sortable
              style={{ width: '180px' }}
            />
            <Column
              field="NewDesign"
              header="Designation"
              sortable
              body={(row) => row.NewDesign || '-'}
              style={{ width: '140px' }}
            />
            <Column
              field="school_name"
              header="From"
              sortable
              style={{
                width: '250px',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              }}
            />
            <Column
              field="newSchlNme"
              header="To"
              sortable
              style={{
                width: '250px',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              }}
            />
            <Column
              field="afterCnsnUpd"
              header="Status"
              sortable
              body={statusBodyTemplate}
              style={{
                width: '250px',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              }}
            />
          </DataTable>
        </div>

        {isSubmitVisible && (
          <div className="my-3 text-end">
            <Button
              type="button"
              label="Submit"
              className="p-button-success p-button-sm rounded"
              onClick={() => setViewDetails(true)}
            />
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        header="Confirmation Message"
        visible={viewDetails}
        onHide={() => setViewDetails(false)}
        draggable={false}
        blockScroll
        resizable={false}
        style={{ width: '50vw' }}
        modal
        headerClassName="dialog-header-secondary"
      >
        <div className="p-3">
          <b>
            Do you want to proceed with changing the counselling for these{' '}
            {pendingCount} staff members?
          </b>

          <div className="text-end mt-4">
            <Button
              type="button"
              label="Yes"
              className="p-button-success p-button-sm me-2 rounded"
              onClick={handleSave}
            />
            <Button
              type="button"
              label="No"
              className="p-button-danger p-button-sm rounded"
              onClick={() => setViewDetails(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TransferFilter;
